import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft } from 'lucide-react';

interface Answer {
  id: string;
  text: string;
  values: string[];
}

interface Question {
  id: number;
  question: string;
  answers: Answer[];
}

interface QuestionScreenProps {
  currentQuestion: number;
  totalQuestions: number;
  question: Question;
  onAnswer: (answer: string) => void;
  onPrevious: () => void;
  canGoBack: boolean;
}

interface ChatMessage {
  type: 'question' | 'answer';
  text: string;
  id: string;
}

export function QuestionScreen({
  currentQuestion,
  totalQuestions,
  question,
  onAnswer,
  onPrevious,
  canGoBack
}: QuestionScreenProps) {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<Answer | null>(null);
  const [showAnswerInChat, setShowAnswerInChat] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const progress = (currentQuestion / totalQuestions) * 100;

  // Add question to chat when it changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setChatHistory(prev => {
        // Check if this question is already in the chat history
        const questionExists = prev.some(msg => msg.id === `q-${question.id}`);
        if (questionExists) {
          return prev; // Don't add duplicate
        }
        return [
          ...prev,
          { type: 'question', text: question.question, id: `q-${question.id}` }
        ];
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [question.id, question.question]);

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    //console.log("chatHistory updated:", chatHistory);
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, showAnswerInChat]);

  const handleSelect = (answer: Answer) => {
    setSelectedAnswer(answer);
    setShowAnswerInChat(true);
    // Add answer to chat history
    setChatHistory(prev => [
      ...prev,
      { type: 'answer', text: answer.text, id: `a-${answer.id}` }
    ]);

    // Trigger next question after delay
    setTimeout(() => {
      onAnswer(answer.values[0]);
      setSelectedAnswer(null);
      setShowAnswerInChat(false);
    }, 800);
  };

  return (
    <div className="flex flex-col">
      {/* Sticky Header with Progress */}
      <div className="sticky top-0 bg-white z-10 px-6 py-4 space-y-3" style={{ boxShadow: '0 2px 8px rgb(0 0 0 / 0.06)' }}>
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              onPrevious();
              setChatHistory(prev => prev.slice(0, -2));
            }}
            disabled={!canGoBack}
            className="flex items-center gap-2 disabled:opacity-30 transition-all active:scale-95 personalitytest"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" strokeWidth={1.5} />
            <span className="text-gray-600">Forrige spørgsmål</span>
          </button>
          <span className="text-gray-500">{currentQuestion}/{totalQuestions}</span>
        </div>
        
        {/* Progress Bar */}
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-ase-orange"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>

      {/* Chat Container */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-6 space-y-4"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="max-w-md mx-auto space-y-4">
          {chatHistory.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={`flex ${message.type === 'answer' ? 'justify-end' : 'justify-start'} items-end gap-2`}
            >
              {/* Asbjørn Avatar - only for questions */}
              {message.type === 'question' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-8 h-8 rounded-full bg-gradient-to-br from-ase-orange/20 to-ase-petroleum/20 flex items-center justify-center flex-shrink-0 mb-1"
                  style={{ boxShadow: 'var(--shadow-soft)' }}
                >
                  <img src="/assets/images/personalityTest/d762c08f76ede3276881e52c59976446704e0faf.png" alt="Ase" className="w-full h-full rounded-full object-cover" />
                </motion.div>
              )}

              <div
                className={`max-w-[80%] p-4 rounded-2xl relative ${
                  message.type === 'question'
                    ? 'bg-white rounded-tl-sm'
                    : 'bg-ase-petroleum text-white rounded-tr-sm'
                }`}
                style={{
                  boxShadow: message.type === 'question' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                {/* Speech bubble tail - only for questions */}
                {message.type === 'question' && (
                  <div 
                    className="absolute -left-2 bottom-2 w-4 h-4 bg-white"
                    style={{
                      clipPath: 'polygon(100% 0, 0 50%, 100% 100%)',
                      filter: 'drop-shadow(-1px 1px 2px rgb(0 0 0 / 0.05))'
                    }}
                  />
                )}
                <p className={message.type === 'answer' ? 'text-white' : 'text-gray-800'}>
                  {message.text}
                </p>
              </div>
            </motion.div>
          ))}

          {/* Answer Options - only show if no answer selected yet */}
          {!showAnswerInChat && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-2 gap-3 pt-2"
              >
                {question.answers.map((answer, index) => (
                  <motion.button
                    key={answer.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    onClick={() => handleSelect(answer)}
                    className="p-4 rounded-xl text-left transition-all active:scale-95 bg-white border border-gray-200 hover:border-ase-petroleum hover:bg-ase-petroleum/5"
                    style={{ boxShadow: '0 2px 4px rgb(0 0 0 / 0.2)' }}
                  >
                    <p className="text-gray-800 text-md leading-snug">{answer.text}</p>
                  </motion.button>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}